import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Select, TextField } from '../ui';
import { apiClient } from '../../api/client';
import { useSession } from '../../context/SessionContext';
import { CHANGE_TYPES } from './constants';
import { classifyError } from './errors';
import PanelState from './PanelState';

/** CKB returns `{result: <string|object>}` where the string is usually JSON. */
function readable(result) {
  if (result == null) return '';
  if (typeof result !== 'string') {
    try {
      return JSON.stringify(result, null, 2);
    } catch {
      return String(result);
    }
  }
  try {
    return JSON.stringify(JSON.parse(result), null, 2);
  } catch {
    return result;
  }
}

/**
 * Impact-Analyse (spec §10.5).
 *
 * Controlled target so the architecture graph can hand a module over
 * ("Impact Analysis" in `NodeDetailsSidebar`) and have it run without the user
 * retyping the path. `runToken` is bumped by the parent to request an
 * automatic run; typing afterwards never re-runs on its own.
 *
 * @param {object} props
 * @param {string} props.target
 * @param {(target: string) => void} props.onTargetChange
 * @param {number} [props.runToken] - increment to trigger one automatic run.
 */
export function ImpactPanel({ target, onTargetChange, runToken = 0 }) {
  const { t } = useTranslation();
  const { sessionId, isConnected } = useSession();

  const [changeType, setChangeType] = useState('modify');
  const [result, setResult] = useState(null);
  const [ranFor, setRanFor] = useState(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const lastToken = useRef(runToken);

  const run = useCallback(
    async (nextTarget, nextChangeType) => {
      const trimmed = (nextTarget || '').trim();
      if (!trimmed) return;
      if (!sessionId) {
        setError(new Error('No active session'));
        return;
      }
      setRunning(true);
      setError(null);
      try {
        const data = await apiClient.prepareChange(sessionId, trimmed, nextChangeType);
        setResult(readable(data.result));
        setRanFor(trimmed);
      } catch (err) {
        setError(err);
        setResult(null);
      } finally {
        setRunning(false);
      }
    },
    [sessionId]
  );

  // One automatic run per token bump — the graph asking for an analysis.
  useEffect(() => {
    if (runToken === lastToken.current) return;
    lastToken.current = runToken;
    if (target?.trim()) run(target, changeType);
  }, [runToken, target, changeType, run]);

  const disabled = !target.trim() || running || !isConnected;

  return (
    <div className="etch-knowledge__impact">
      <form
        className="etch-knowledge__impact-form"
        onSubmit={(event) => {
          event.preventDefault();
          run(target, changeType);
        }}
      >
        <TextField
          label={t('knowledge.impactTarget')}
          placeholder={t('ide.symbolPlaceholder')}
          value={target}
          onChange={(event) => onTargetChange(event.target.value)}
          className="etch-knowledge__impact-target"
        />
        <Select
          label={t('knowledge.impactChangeType')}
          value={changeType}
          onChange={(event) => setChangeType(event.target.value)}
          options={CHANGE_TYPES.map((value) => ({ value, label: t(`changeType.${value}`) }))}
          className="etch-knowledge__impact-type"
        />
        <Button type="submit" disabled={disabled} loading={running}>
          {t('ide.analyzeImpact')}
        </Button>
      </form>

      <p className="etch-visually-hidden" role="status" aria-live="polite">
        {running
          ? t('knowledge.impactRunning', { target })
          : ranFor
            ? t('knowledge.impactReady', { target: ranFor })
            : ''}
      </p>

      {!isConnected && !running && !error && (
        <PanelState status="noSource" title={t('ide.connectPrompt')} compact />
      )}

      {running && result === null && <PanelState status="loading" title={t('knowledge.impactRunning', { target })} />}

      {error && (
        <PanelState
          status={classifyError(error)}
          error={error}
          onRetry={() => run(target, changeType)}
        />
      )}

      {result !== null && !error && (
        <section className="etch-knowledge__impact-result" aria-label={t('knowledge.impactResult', { target: ranFor })}>
          <h3 className="etch-knowledge__impact-result-title">
            {t('knowledge.impactResult', { target: ranFor })}
          </h3>
          <div className="etch-knowledge__preview-body" data-variant="raw" data-busy={running || undefined}>
            <pre>{result}</pre>
          </div>
        </section>
      )}

      {result === null && !running && !error && isConnected && (
        <PanelState status="empty" title={t('knowledge.impactHint')} />
      )}
    </div>
  );
}

export default ImpactPanel;
